import { CSSProperties, useEffect } from "react";
import { PLAYBOOK_TYPE_INFO, PlaybookType } from "modules/Const";
import CampaignView from "./CampaignView";
import { useHistory, useParams } from "react-router-dom";
import useCampaign from "Hooks/useCampaign";
import DebugBox from "./shareable/DebugBox";
import Loader from "elements/Loader";
import Banner from "elements/Banner";
import ButtonSymphony from "elements/ButtonSymphony";

interface MarketingFlow2Props {
    playbookType?: PlaybookType
}

/**
 * MarketingFlow2 component that handles campaign creation and editing
 * Supports two entry points:
 * 1. New campaign with a specified playbookType
 * 2. Existing draft campaign with a draftId
 */
const MarketingFlow2 = ({
    playbookType: playbookTypeProp
}: MarketingFlow2Props) => {
    const history = useHistory();
    const params = useParams<{
        playbookType: string,
        draftId?: string,
    }>();
    const draftCampaignId = Number(params.draftId);
    const isDraftMode = Boolean(draftCampaignId);

    const {
        isLoadingCampaign,
        playbookType,
        getCampaign,
        initializeCampaignState,
        resetCampaignState,
        campaignInitialized,
    } = useCampaign();

    const initializeCampaignData = async () => {
        resetCampaignState();

        try {
            if (!isDraftMode) {
                initializeCampaignState({
                    playbookType: playbookTypeProp || (params.playbookType as PlaybookType)
                });
            } else {
                await getCampaign(draftCampaignId);
            }
        } catch (error) {
            console.error('Error initializing campaign data in marketing flow:', error);
        }
    }

    useEffect(() => {
        initializeCampaignData();
    }, []);

    // Show loading state while fetching campaign data in draft mode
    if (isDraftMode && isLoadingCampaign) {
        return (
            <Loader
                className="marketingFlow2Loader"
                title="Loading campaign data..."
                size={44}
                isCentered
            />
        )
    }

    const renderCampaignErrorBanner = () => {
        return (
            <div
                className="marketingFlow2ErrorBannerContainer"
                style={sty.marketingFlow2ErrorBannerContainer}
            >
                <Banner
                    className="marketingFlow2ErrorBanner"
                    color="red"
                    title={isDraftMode ? "Campaign draft not found" : "Invalid campaign type"}
                    style={{ gap: 8 }}
                >
                    {isDraftMode ?
                        <div>
                            It's possible that your draft has been published or has been deleted.
                        </div>
                        :
                        <div>
                            Please specify a valid playbook type
                        </div>
                    }

                    <ButtonSymphony
                        className="marketingFlow2ErrorBannerButton"
                        onClick={() => history.push('/marketing')}
                        iconLeft="arrowLeft"
                        variant="outlined"
                        color="grey"
                    >
                        Back to marketing flow
                    </ButtonSymphony>
                </Banner>
            </div>
        )
    }

    // Don't render if we don't have a valid playbook type
    if (!campaignInitialized || !playbookType) {
        return renderCampaignErrorBanner();
    }

    return (
        <div
            className="marketingFlow2Component"
            style={sty.marketingFlow2Component}
        >
            {/* <DebugBox
                alwaysShow
                data={{
                    startDate
                    // playbook: effectivePlaybookType,
                    // playbookStepNames,
                    // defaultStep,
                    // isDraftMode,
                    // currentStep
                }}
            /> */}
            <CampaignView
                className="campaignViewMarketingFlow2"
                defaultsToFirstStep={!isDraftMode}
                defaultsToLastStep={isDraftMode}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    marketingFlow2Component: {
        width: '100%',
    },
    marketingFlow2ErrorBannerContainer: {
        padding: 24,
    },
};

export default MarketingFlow2;
